import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { loginUser, resetUser } from '../../redux/user/userSlice';
import UserTheme from '../theme/UserTheme';
import {
  Container,
  FormPane,
  DesktopFormContainer,
  CenterContainer,
  Back,
  AuthForm,
  MobileContainer,
  MobileFormPane,
  MobileFormCopy,
  MobileFormContent,
  MobileFormCard,
} from './elements';
import Head from '../common/Head';
import UserTextInput from '../common/user/UserTextInput';
import UserPasswordInput from '../common/user/UserPasswordInput';
import {
  UserInputsContainer,
  UserInputError,
  UserInputRow,
} from '../common/user/elements';
import Logo from '../common/Logo';
import Button from '../common/Button';
import {
  Title,
  Copy,
} from '../common/elements';
import {
  $white,
  $primaryFont,
  $black,
} from '../../styles';

const ForgotPasswordButton = styled(Link)`
  color: ${$black};
  font-family: ${$primaryFont};
  text-decoration: underline;
`;

const LoginForm = () => {
  const { error: authError } = useSelector((state) => state.user);
  const { handleSubmit, errors, control } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const location = useLocation();
  const dispatch = useDispatch();

  // Referrer is the path the user was trying to reach before getting redirected to /login
  const onSubmit = async (data) => dispatch(
    loginUser(
      data,
      location.state && location.state.referrer,
    ),
  );

  /**
   * Generates an object for pushing onto history
   * that preserves current location state.
   */
  const preserveState = (pathname) => {
    const { state } = location;
    return { pathname, state };
  };

  return (
    <FormPane>
      <Back />
      <CenterContainer>
        <DesktopFormContainer>
          <Title>Welcome Back</Title>
          <Copy>
            Get exclusive access to premium events &amp; experiences.
          </Copy>
          <AuthForm>
            <UserInputsContainer margin="2rem 0 0 0">
              <UserInputRow>
                <UserTextInput
                  control={control}
                  placeholder="email@company.com"
                  label="Email Address"
                  name="email"
                  rules={{ required: 'Required' }}
                  error={errors.email}
                  type="email"
                />
              </UserInputRow>
              <UserInputRow>
                <UserPasswordInput
                  control={control}
                  placeholder="Enter your password"
                  label="Password"
                  name="password"
                  rules={{ required: 'Required' }}
                  error={errors.password}
                />
              </UserInputRow>
            </UserInputsContainer>
            {
              authError && <UserInputError>{ authError }</UserInputError>
            }
            <Button
              fullWidth
              margin="2rem 0 0.75rem 0"
              onClick={handleSubmit(onSubmit)}
            >
              Login
            </Button>
            <ForgotPasswordButton to={preserveState('/forgot-password')}>
              Forgot your password?
            </ForgotPasswordButton>
          </AuthForm>
        </DesktopFormContainer>
      </CenterContainer>
    </FormPane>
  );
};

const MobileLoginForm = () => {
  const { error: authError } = useSelector((state) => state.user);
  const { handleSubmit, errors, control } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const location = useLocation();
  const dispatch = useDispatch();

  // Referrer is the path the user was trying to reach before getting redirected to /login
  const onSubmit = async (data) => dispatch(
    loginUser(
      data,
      location.state && location.state.referrer,
    ),
  );

  const preserveState = (pathname) => {
    const { state } = location;
    return { pathname, state };
  };

  return (
    <MobileFormPane>
      <MobileFormContent>
        <Logo to="/" />
        <MobileFormCopy>
          <Title color={$white}>Welcome Back!</Title>
          <Copy>
            Get exclusive access to premium events &amp; experiences.
          </Copy>
        </MobileFormCopy>
      </MobileFormContent>
      <MobileFormCard>
        <AuthForm>
          <UserInputsContainer margin="0">
            <UserInputRow>
              <UserTextInput
                control={control}
                placeholder="email@company.com"
                label="Email Address"
                name="email"
                rules={{ required: 'Required' }}
                error={errors.email}
                type="email"
              />
            </UserInputRow>
            <UserInputRow>
              <UserPasswordInput
                control={control}
                placeholder="Enter your password"
                label="Password"
                name="password"
                rules={{ required: 'Required' }}
                error={errors.password}
              />
            </UserInputRow>
          </UserInputsContainer>
          {
            authError && <UserInputError>{ authError }</UserInputError>
          }
          <Button
            fullWidth
            margin="2rem 0 0.75rem 0"
            onClick={handleSubmit(onSubmit)}
          >
            Login
          </Button>
          <ForgotPasswordButton to={preserveState('/forgot-password')}>
            Forgot your password?
          </ForgotPasswordButton>
        </AuthForm>
      </MobileFormCard>
    </MobileFormPane>
  );
};

export default () => {
  const dispatch = useDispatch();

  // Clear errors, statuses, etc... when this page unmounts
  useEffect(() => dispatch(resetUser()), [dispatch]);
  return (
    <UserTheme>
      <Head title="Login" />
      <Container>
        <LoginForm />
      </Container>
      <MobileContainer>
        <MobileLoginForm />
      </MobileContainer>
    </UserTheme>
  );
};
